import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ArrowRight, Home } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 15;

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", attempts: 0, data: null });

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let attempt = 0;
    const poll = async () => {
      if (cancelled) return;
      attempt += 1;
      try {
        const d = await api.paymentStatus(sessionId);
        if (d.payment_status === "paid") {
          setState({ status: "paid", attempts: attempt, data: d });
          toast.success("Welcome to EIGOO Forge", { description: "Your subscription is active." });
          return;
        }
        if (["failed", "expired"].includes(d.payment_status)) {
          setState({ status: "failed", attempts: attempt, data: d });
          return;
        }
        if (attempt >= MAX_ATTEMPTS) {
          setState({ status: "timeout", attempts: attempt, data: d });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (attempt < MAX_ATTEMPTS) setTimeout(poll, POLL_INTERVAL_MS);
        else setState({ status: "error", attempts: attempt, data: null });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4" data-testid="payment-success-page">
      <div className="card-sharp p-10 max-w-md w-full text-center space-y-6">
        {state.status === "polling" && (
          <>
            <Loader2 className="w-12 h-12 gold-text mx-auto animate-spin" strokeWidth={1.5} />
            <h1 className="text-2xl font-black tracking-tighter">Confirming your subscription…</h1>
            <p className="text-sm text-gray-400">Stripe is finalizing the payment. This usually takes a few seconds.</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto" strokeWidth={1.5} />
            <div className="label-tag gold-text">EIGOO Forge</div>
            <h1 className="text-3xl font-black tracking-tighter">You're in.</h1>
            <p className="text-sm text-gray-400">
              Your Content Subscription is active. Welcome aboard — a receipt is on its way to your inbox.
            </p>
            <Link to="/bridge" className="btn-gold inline-flex items-center gap-2 w-full justify-center" data-testid="payment-success-continue">
              Continue to Console <ArrowRight className="w-4 h-4" />
            </Link>
          </>
        )}
        {(state.status === "failed" || state.status === "timeout" || state.status === "error") && (
          <>
            <XCircle className="w-14 h-14 text-red-400 mx-auto" strokeWidth={1.5} />
            <h1 className="text-2xl font-black tracking-tighter">
              {state.status === "timeout" ? "Still processing…" : "Something went wrong"}
            </h1>
            <p className="text-sm text-gray-400">
              {state.status === "timeout"
                ? "Your payment is still confirming with Stripe. You'll receive an email shortly."
                : "The payment didn't complete. No charge was made if this shows an error."}
            </p>
            <div className="flex gap-2">
              <Link to="/public-forge" className="btn-ghost-gold flex-1 text-xs" data-testid="payment-success-retry">Try Again</Link>
              <Link to="/bridge" className="btn-gold flex-1 text-xs inline-flex items-center justify-center gap-2" data-testid="payment-success-home">
                <Home className="w-3.5 h-3.5" /> Home
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4" data-testid="payment-cancel-page">
      <div className="card-sharp p-10 max-w-md w-full text-center space-y-6">
        <XCircle className="w-14 h-14 gold-text mx-auto opacity-70" strokeWidth={1.5} />
        <div className="label-tag">Checkout Cancelled</div>
        <h1 className="text-2xl font-black tracking-tighter">No worries — no charge made.</h1>
        <p className="text-sm text-gray-400">
          You can return anytime to subscribe. If you had a question that stopped you, we're happy to help.
        </p>
        <div className="flex gap-2">
          <Link to="/public-forge" className="btn-gold flex-1 text-xs inline-flex items-center justify-center gap-2" data-testid="payment-cancel-retry">
            Back to Pricing <ArrowRight className="w-3.5 h-3.5" />
          </Link>
          <a href="mailto:support@eigooinc.net" className="btn-ghost-gold flex-1 text-xs" data-testid="payment-cancel-contact">
            Contact Us
          </a>
        </div>
      </div>
    </div>
  );
}
